import type { ApiResponse, PagedResponseDto } from "@/types/common";
import type {
  AdminWalletAdjustDto,
  MemberTransferPreviewDto,
  SettlementFilterParams,
  SettlementResponseDto,
  WalletResponseDto,
  WalletTransactionFilterParams,
  WalletTransactionResponseDto,
  WithdrawalRequestDto,
  ProcessSettlementDto,
} from "@/types/wallet.dto";
import { apiClient, unwrapApiResponse } from "./httpClient";
import type { WalletOwnerType } from "@/types/enums";

export const walletsService = {
  getMyWallet(): Promise<WalletResponseDto> {
    return unwrapApiResponse(apiClient.get<ApiResponse<WalletResponseDto>>("/Wallets"));
  },

  getWallet(ownerType: WalletOwnerType, entityId: number): Promise<WalletResponseDto> {
    return unwrapApiResponse(
      apiClient.get<ApiResponse<WalletResponseDto>>(`/Wallets/${ownerType}/${entityId}`),
    );
  },

  getUserWallet(userId: string): Promise<WalletResponseDto> {
    return unwrapApiResponse(
      apiClient.get<ApiResponse<WalletResponseDto>>(`/Wallets/admin/users/${userId}`),
    );
  },

  adminAdjust(dto: AdminWalletAdjustDto): Promise<WalletResponseDto> {
    return unwrapApiResponse(
      apiClient.post<ApiResponse<WalletResponseDto>>("/Wallets/admin/adjust", dto),
    );
  },

  previewTransferRecipient(memberCode: string): Promise<MemberTransferPreviewDto> {
    return unwrapApiResponse(
      apiClient.get<ApiResponse<MemberTransferPreviewDto>>(
        `/Wallets/transfer/preview/${encodeURIComponent(memberCode)}`,
      ),
    );
  },

  getTransactions(
    walletId: number,
    filter: WalletTransactionFilterParams = {},
  ): Promise<PagedResponseDto<WalletTransactionResponseDto>> {
    return unwrapApiResponse(
      apiClient.get<ApiResponse<PagedResponseDto<WalletTransactionResponseDto>>>(
        `/Wallets/${walletId}/transactions`,
        { params: filter },
      ),
    );
  },

  getMyTransactions(
    filter: WalletTransactionFilterParams = {},
  ): Promise<PagedResponseDto<WalletTransactionResponseDto>> {
    return unwrapApiResponse(
      apiClient.get<ApiResponse<PagedResponseDto<WalletTransactionResponseDto>>>(
        "/Wallets/transactions/me",
        { params: filter },
      ),
    );
  },

  getAdminTransactions(
    filter: WalletTransactionFilterParams = {},
  ): Promise<PagedResponseDto<WalletTransactionResponseDto>> {
    return unwrapApiResponse(
      apiClient.get<ApiResponse<PagedResponseDto<WalletTransactionResponseDto>>>(
        "/Wallets/admin/transactions",
        { params: filter },
      ),
    );
  },

  withdraw(walletId: number, dto: WithdrawalRequestDto): Promise<boolean> {
    return unwrapApiResponse(
      apiClient.post<ApiResponse<boolean>>(`/Wallets/${walletId}/withdraw`, dto),
    );
  },

  getSettlements(filter: SettlementFilterParams = {}): Promise<PagedResponseDto<SettlementResponseDto>> {
    return unwrapApiResponse(
      apiClient.get<ApiResponse<PagedResponseDto<SettlementResponseDto>>>("/Wallets/settlements", {
        params: filter,
      }),
    );
  },

  processSettlement(settlementId: number, dto: ProcessSettlementDto): Promise<boolean> {
    return unwrapApiResponse(
      apiClient.post<ApiResponse<boolean>>(`/Wallets/settlements/${settlementId}/process`, dto),
    );
  },

  failSettlement(settlementId: number, reason: string): Promise<boolean> {
    return unwrapApiResponse(
      apiClient.post<ApiResponse<boolean>>(`/Wallets/settlements/${settlementId}/fail`, JSON.stringify(reason), {
        headers: { "Content-Type": "application/json" },
      }),
    );
  },
};
